import { config } from "@/config";
import { Api } from "@repo/shared/api";
import { ControllerSchemas } from "@repo/types";
import { TOTP } from "@repo/lib/totp";
import { getToken } from "./auth";
import "./functions";
import { wait } from "@repo/lib/wait";
import * as Sentry from "@sentry/browser";
import z from "zod";

const controllerMethods = Object.create(null) as Record<
  string,
  z.ZodType | undefined
>;

for (const controller of Object.keys(
  ControllerSchemas
) as (keyof typeof ControllerSchemas)[]) {
  Object.assign(controllerMethods, ControllerSchemas[controller]);
}

export const apiService = new Api({
  baseUrl: config.urls.api.replace(/\/+$/, ""),
  baseApiParams: { cache: "no-store" },
  responseBodyFilter: (body, funcName) => {
    const type = controllerMethods[funcName];
    if (type) {
      const output = type.safeParse(body);
      if (!output.success) {
        const message = `Failed to parse response from apiService.v1.${funcName}: ${output.error}`;
        console.warn(message);
        Sentry.captureException(new Error(message));
      }
      return output.success ? output.data : body;
    } else {
      const message = `No schema for API method apiService.v1.${funcName}`;
      console.warn(message);
      Sentry.captureException(new Error(message));
      return body;
    }
  },
  async securityWorker(_: void | null) {
    let token: string | undefined;

    // retry for token: cycle for 60 seconds
    for (let i = 0; i < 60; i++) {
      token = await getToken();
      if (token) break;
      await wait(1000);
    }
    if (!token) {
      console.log("Error: No token");
      throw new Error("No token provided, trying to access secure endpoint.");
    }
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  },
});

// exposed adminNotify method
export const runAdminNotify = async function (
  type: string,
  data: Record<string, string>,
  key: string = "EMULATION_NOTIFY_KEY"
) {
  return await apiService.v1.adminNotify({ key, type, data });
};

// exposed backup method
export const runBackup = async function (
  key: string,
  totp?: string,
  tenantId?: string
) {
  const token = await getToken();
  const response = await fetch(
    config.urls.api + "/v1/superadmin/backups/backup",
    {
      method: "POST",
      body: JSON.stringify({ key, totp, tenant: tenantId }),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return await response.blob();
};

export const runRestore = async function (
  key: string,
  totp: string | null | undefined,
  data: string
) {
  return await apiService.v1.restore({
    key,
    totp,
    data,
  });
};

export const runIntegrityCheck = async function (
  key: string,
  tenantId?: string
) {
  const { data: tests } = await apiService.v1.integrityCheck({
    key,
    tenant: tenantId,
  });
  return Object.assign(tests, { success: !tests.length });
};

export const runClearCache = async function (key: string) {
  return (await apiService.v1.clearCache({ key })).data;
};

// expose methods on window
Object.assign(window, {
  runAdminNotify,
  runBackup,
  runRestore,
  runIntegrityCheck,
  runClearCache,
  totp: TOTP,
});
